import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { AdminLayout } from '../../components/layout/AdminLayoutNew';
import { MapEditor } from '../../components/MapEditor';
import { PositionCaptureMode } from '../../components/PositionCaptureMode';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { ArrowLeft, MapPin, Home, Crosshair } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminMapEditor() {
  const { projectId } = useParams();
  const [project, setProject] = useState(null);
  const [projects, setProjects] = useState([]);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProjectId, setSelectedProjectId] = useState(projectId || '');
  const [mapImageUrl, setMapImageUrl] = useState('');
  const [captureMode, setCaptureMode] = useState(false);
  const [existingPositions, setExistingPositions] = useState([]);
  const { getAuthHeaders } = useAuth();

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/projects`, { headers: getAuthHeaders() });
      setProjects(response.data.filter(p => p.id === 'bakrajo-hills'));
      if (!selectedProjectId && response.data.length > 0) {
        setSelectedProjectId(response.data[0].id);
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
      toast.error('Failed to load projects');
    }
  };

  const isBakrajoHills = selectedProjectId === 'bakrajo-hills';

  const fetchProjectData = useCallback(async () => {
    if (!selectedProjectId) return;
    
    setLoading(true);
    try {
      const requests = [
        axios.get(`${API_URL}/api/projects/${selectedProjectId}`, { headers: getAuthHeaders() }),
      ];
      
      if (selectedProjectId === 'bakrajo-hills') {
        requests.push(axios.get(`${API_URL}/api/admin/sitemap-units/bakrajo-hills`, { headers: getAuthHeaders() }));
      } else {
        requests.push(axios.get(`${API_URL}/api/units?project_id=${selectedProjectId}`, { headers: getAuthHeaders() }));
      }
      
      const results = await Promise.all(requests);
      setProject(results[0].data);
      setMapImageUrl(results[0].data.map_image || '');
      
      if (selectedProjectId === 'bakrajo-hills') {
        // Transform sitemap units to be compatible with MapEditor format
        // Only include units that have actual positions on the map
        const smUnits = results[1].data
          .filter(u => u.position_x != null && u.position_y != null)
          .map(u => ({
            id: `sm-${u.villa_type}-${u.villa_number}`,
            unit_number: String(u.villa_number),
            project_id: 'bakrajo-hills',
            status: u.status || 'available',
            price: u.price || '',
            position_x: u.position_x,
            position_y: u.position_y,
            buyer_name: u.buyer_name || '',
            phone: u.phone || '',
            villa_type: u.villa_type,
            sqm: u.sqm,
            contract_number: u.contract_number || '',
            _isSitemap: true,
            _villaNumber: u.villa_number,
          }));
        setUnits(smUnits);
      } else {
        setUnits(results[1].data);
      }
    } catch (error) {
      console.error('Error fetching project:', error);
      toast.error('Failed to load project data');
    } finally {
      setLoading(false);
    }
  }, [selectedProjectId, getAuthHeaders]);

  useEffect(() => {
    fetchProjects();
  }, []);

  useEffect(() => {
    if (selectedProjectId) {
      fetchProjectData();
    }
  }, [selectedProjectId, fetchProjectData]);

  const handleSaveUnit = async (unitId, unitData) => {
    const headers = getAuthHeaders();
    
    // Check if this is a sitemap unit
    if (unitId && unitId.startsWith('sm-')) {
      const parts = unitId.split('-');
      const villaType = parts[1];
      const villaNum = parseInt(unitData.unit_number);
      await axios.put(
        `${API_URL}/api/admin/sitemap-units/bakrajo-hills/${villaNum}`,
        { status: unitData.status, buyer_name: unitData.buyer_name || '', phone: unitData.phone || '', price: unitData.price, contract_number: unitData.contract_number || '', villa_type: villaType },
        { headers }
      );
    } else if (unitId) {
      await axios.put(`${API_URL}/api/units/${unitId}`, unitData, { headers });
    } else {
      await axios.post(`${API_URL}/api/units`, unitData, { headers });
    }
    
    fetchProjectData();
  };

  const handleDeleteUnit = async (unitId) => {
    if (unitId && unitId.startsWith('sm-')) {
      toast.error('Sitemap units cannot be deleted from Map Editor');
      return;
    }
    await axios.delete(`${API_URL}/api/units/${unitId}`, { headers: getAuthHeaders() });
    fetchProjectData();
  };

  const handleUpdateUnitPosition = async (unitId, positionX, positionY) => {
    const headers = getAuthHeaders();
    
    if (unitId && unitId.startsWith('sm-')) {
      // Extract villa type and number from id like "sm-A-10"
      const parts = unitId.split('-');
      const villaType = parts[1];
      const villaNum = parseInt(parts[parts.length - 1]);
      await axios.put(
        `${API_URL}/api/admin/sitemap-units/bakrajo-hills/${villaNum}`,
        { position_x: positionX, position_y: positionY, villa_type: villaType },
        { headers }
      );
    } else {
      await axios.put(
        `${API_URL}/api/units/${unitId}`,
        { position_x: positionX, position_y: positionY },
        { headers }
      );
    }
    fetchProjectData();
  };

  const handleUpdateMapImage = async () => {
    if (!mapImageUrl) {
      toast.error('Please enter a map image URL');
      return;
    }

    try {
      await axios.put(
        `${API_URL}/api/projects/${selectedProjectId}`,
        { map_image: mapImageUrl },
        { headers: getAuthHeaders() }
      );
      toast.success('Map image updated');
      fetchProjectData();
    } catch (error) {
      toast.error('Failed to update map image');
    }
  };

  // Fetch existing positions from DB for duplicate detection
  const fetchExistingPositions = useCallback(async () => {
    if (selectedProjectId !== 'bakrajo-hills') return;
    try {
      const headers = getAuthHeaders();
      const res = await axios.get(`${API_URL}/api/admin/map-positions/${selectedProjectId}`, { headers });
      setExistingPositions(res.data.positions || []);
    } catch (error) {
      console.error('Error fetching positions:', error);
    }
  }, [selectedProjectId, getAuthHeaders]);

  useEffect(() => {
    if (captureMode) fetchExistingPositions();
  }, [captureMode, fetchExistingPositions]);

  const handleSaveCapturedPositions = async (positions) => {
    try {
      const headers = getAuthHeaders();
      let saved = 0;
      for (const pos of positions) {
        await axios.post(`${API_URL}/api/admin/capture-position/bakrajo-hills`, {
          unit_number: pos.unit_number,
          villa_type: pos.villa_type,
          position_x: pos.x,
          position_y: pos.y,
          zone: pos.zone,
        }, { headers });
        saved++;
      }
      toast.success(`Saved ${saved} positions to database`);
      fetchExistingPositions();
      fetchProjectData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save positions');
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-map-editor-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <Link to="/admin/units" className="inline-flex items-center gap-2 text-white/60 hover:text-[#D4AF37] transition-colors mb-2">
              <ArrowLeft className="w-4 h-4" />
              <span className="text-sm">Back to Units</span>
            </Link>
            <h1 className="font-['Playfair_Display'] text-3xl font-semibold text-white">Unit Map Editor</h1>
            <p className="text-white/60 mt-1">Add, move, and edit unit markers on the project map</p>
          </div>
        </div>

        {/* Project Selector */}
        <Card className="bg-[#0A0A0A] border-white/10">
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-end gap-4">
              <div className="flex-1 space-y-2">
                <Label className="text-white/70">Select Project</Label>
                <Select value={selectedProjectId} onValueChange={setSelectedProjectId}>
                  <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="project-selector">
                    <SelectValue placeholder="Select a project" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0A0A0A] border-white/10">
                    {projects.map((p) => (
                      <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {project && (
              <div className="mt-4 flex items-center gap-4 text-sm">
                <div className="flex items-center gap-2 text-white/60">
                  <MapPin className="w-4 h-4 text-[#D4AF37]" />
                  <span>{project.location}</span>
                </div>
                <div className="flex items-center gap-2 text-white/60">
                  <Home className="w-4 h-4 text-emerald-500" />
                  <span>{units.length} units on map</span>
                  {isBakrajoHills && <span className="text-[#D4AF37] text-xs">(Sitemap Data)</span>}
                </div>
                {isBakrajoHills && (
                  <button
                    onClick={() => setCaptureMode(!captureMode)}
                    className={`ml-auto flex items-center gap-2 px-4 py-1.5 text-xs uppercase tracking-wider font-medium transition-all ${
                      captureMode
                        ? 'bg-[#D4AF37] text-black'
                        : 'border border-[#D4AF37]/50 text-[#D4AF37] hover:bg-[#D4AF37]/10'
                    }`}
                    data-testid="toggle-capture-mode-btn"
                  >
                    <Crosshair className="w-4 h-4" />
                    {captureMode ? 'Exit Capture Mode' : 'Position Capture Mode'}
                  </button>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Map Editor or Capture Mode */}
        {loading ? (
          <div className="flex items-center justify-center h-96">
            <div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : project && captureMode && isBakrajoHills ? (
          <PositionCaptureMode
            mapImage={project.map_image}
            onSavePositions={handleSaveCapturedPositions}
            existingPositions={existingPositions}
          />
        ) : project ? (
          <MapEditor
            mapImage={project.map_image}
            units={units}
            projectId={selectedProjectId}
            onSaveUnit={handleSaveUnit}
            onDeleteUnit={handleDeleteUnit}
            onUpdateUnitPosition={handleUpdateUnitPosition}
          />
        ) : (
          <div className="flex flex-col items-center justify-center h-96 text-center">
            <MapPin className="w-16 h-16 text-white/20 mb-4" />
            <h3 className="text-white text-xl mb-2">No Project Selected</h3>
            <p className="text-white/50">Select a project from the dropdown above to edit its unit map</p>
          </div>
        )}

        {/* Instructions */}
        <Card className="bg-[#0A0A0A] border-white/10">
          <CardHeader>
            <CardTitle className="text-white text-lg">How to Use</CardTitle>
          </CardHeader>
          <CardContent className="text-white/60 text-sm space-y-2">
            <p><strong className="text-white">1. Add Units:</strong> Click "Add Unit Marker" then click on the map where you want to place the unit.</p>
            <p><strong className="text-white">2. Move Units:</strong> Drag any marker to reposition it on the map.</p>
            <p><strong className="text-white">3. Edit Units:</strong> Double-click any marker to edit its details (price, status, etc.).</p>
            <p><strong className="text-white">4. Position Capture:</strong> Use "Position Capture Mode" to quickly click and assign positions to multiple units.</p>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
